import {getColorByPath, ThemeColorPath} from 'platform/foundation';
import {css, useTheme} from 'styled-components';

import {KeyboardEvent, forwardRef} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

export interface ColorChipProps extends TestIdProps {
  label: string;
  value: boolean;
  color: ThemeColorPath;
  isDisabled?: boolean;
  isFocused?: boolean;
  onChange?: (value: boolean) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  tabIndex?: number;
  ariaLabel?: string;
}

export const ColorChip = forwardRef<HTMLDivElement, ColorChipProps>((props, ref) => {
  const theme = useTheme();
  const resolvedColor = getColorByPath(props.color, theme) ?? theme.colors.general.accent;
  const isSelected = props.value;

  const handleClick = () => {
    if (!props.isDisabled) {
      props.onChange?.(!props.value);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (props.isDisabled) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      props.onChange?.(!props.value);
    }

    props.onKeyDown?.(event);
  };

  return (
    <div
      ref={ref}
      role="checkbox"
      aria-checked={isSelected}
      aria-disabled={props.isDisabled}
      aria-label={props.ariaLabel ?? props.label}
      tabIndex={props.tabIndex ?? -1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid={suffixTestId('colorChip', props)}
      css={css`
        display: inline-flex;
        align-items: center;
        padding: 4px 12px;
        border-radius: ${({theme}) => theme.radii.large};
        font-size: ${({theme}) => theme.fontSizes.text.xSmall};
        font-weight: ${({theme}) => theme.fontWeights.medium};
        cursor: ${props.isDisabled ? 'not-allowed' : 'pointer'};
        user-select: none;
        border: 2px solid ${resolvedColor};
        transition:
          background-color 0.15s ease,
          opacity 0.15s ease;
        background-color: ${isSelected ? resolvedColor : 'transparent'};
        color: ${isSelected ? theme.colors.general.white : resolvedColor};
        opacity: ${isSelected ? 1 : 0.5};

        ${props.isDisabled &&
        css`
          opacity: 0.4;
          pointer-events: none;
        `}

        ${props.isFocused &&
        css`
          outline: ${theme.outline.default} ${theme.colors.general.accent};
          outline-offset: ${theme.outline.offset};
        `}
      `}
    >
      {props.label}
    </div>
  );
});
